import { Container, Graphics, Text, TextStyle, FillGradient, Rectangle } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W, SCRIM, FONT_FAMILY } from '../app/theme';
import { viewport } from '../app/viewport';
import { Button, makeText, sceneBackground, iconSprite } from '../ui/widgets';
import { TextField } from '../ui/TextField';
import { animate, scaleIn, linear, prefersReducedMotion } from '../app/anim';
import { enterFade } from './common';
import type { AppContext } from '../app/AppContext';
import { RoomName } from '../net/enums';
import { MessageTag } from '../net/messageTags';
import { decodeLoadLoginResult } from '../net/codec/login';
import { encodeForgotPassword } from '../net/codec/register';
import { gameState } from '../state/GameState';
import { hapticNotification, isLocalDevelopment } from '../platform/telegram';
import {
  loadLoginCredentials,
  saveLoginCredentials,
  type StoredLoginCredentials,
} from '../platform/storage';
import { announceStatus } from '../ui/accessibility';

export interface InitialLoginCredentials {
  username: string;
  password: string;
  auto: boolean;
}

export interface LoginSceneOptions {
  /** Disable persisted-credential loading for deterministic local visual fixtures. */
  useStoredCredentials?: boolean;
}

/**
 * Credentials supplied by dev-multi are accepted only after the caller has established that this is
 * a loopback Vite development build. Normal and hosted clients always start with blank fields.
 */
export function loginCredentialsFromSearch(search: string, allowDevHarness: boolean): InitialLoginCredentials {
  const result: InitialLoginCredentials = { username: '', password: '', auto: false };
  if (!allowDevHarness) return result;

  const p = new URLSearchParams(search);
  const client = p.get('client')?.trim().toLowerCase();
  if (client !== 'a' && client !== 'b') return result;

  const user = p.get('user')?.trim();
  const pass = p.get('pass');
  if (user) result.username = user;
  if (pass !== null) result.password = pass;
  result.auto = Boolean(result.username && result.password);
  return result;
}

/**
 * The login screen, rendered in Pixi (port of the original DOM login). The client is already
 * connected by main.ts; this scene runs the LoadLogin→Login handshake and, on success, navigates
 * to the main menu. Failures surface as a global SendMessage(0) dialog.
 */
export class LoginScene extends Scene {
  private username!: TextField;
  private password!: TextField;
  private loginBtn!: Button;
  private status!: ReturnType<typeof makeText>;
  private forgotOverlay!: Container;
  private forgotEmail?: TextField;
  private built = false;
  private usernameLocked = false;
  private savedCredentials: StoredLoginCredentials | null = null;
  private useSavedCredentials = false;
  private savedAutoLoginStarted = false;
  private loginInProgress = false;
  private loginWhenReady = false;
  private boundUsername: string | null = null;

  constructor(
    private readonly ctx: AppContext,
    private readonly options: LoginSceneOptions = {},
  ) {
    super();
  }

  onEnter(): void {
    const creds = LoginScene.credsFromUrl();
    const hasExplicitDevCredentials = Boolean(creds.username || creds.password);
    this.useSavedCredentials = !hasExplicitDevCredentials && (this.options.useStoredCredentials ?? true);
    this.build(creds.username, creds.password);

    this.track(
      this.ctx.client.on(MessageTag.LoadLogin, (reader) => {
        const result = decodeLoadLoginResult(reader);
        if (!result.username) return;

        // The server-bound identity is authoritative over URL prefill or anything typed locally.
        this.boundUsername = result.username;
        this.usernameLocked = result.shouldLockUsername;
        this.username.value = result.username;
        this.username.setReadonly(this.usernameLocked);
        this.trySavedAutoLogin();
      }),
    );

    this.track(this.ctx.client.onOpen(() => {
      if (this.ctx.scenes.active !== this || !this.ctx.client.isReady) return;
      this.ctx.client.loadLogin();
      this.resumeLoginWhenReady();
    }));
    this.track(this.ctx.client.onAvailability((info) => {
      if (this.ctx.scenes.active !== this || info.state !== 'ready') return;
      this.ctx.client.loadLogin();
      this.resumeLoginWhenReady();
    }));

    if (this.ctx.client.isReady) this.ctx.client.loadLogin();
    if (this.useSavedCredentials) void this.loadSavedCredentialsForAutoLogin();

    // The loopback-only two-client harness supplies both fields and needs no manual click.
    if (creds.auto) {
      this.setStatus('ورود خودکار…');
      this.requestLogin();
    }
  }

  /**
   * Re-flow on viewport-height change. The Telegram fullscreen request resolves *after* boot, so this
   * scene (the first one) is built at the pre-fullscreen height and must rebuild at the taller one.
   * Typed input is preserved across the rebuild.
   */
  onResize(): void {
    this.build(this.username?.value ?? '', this.password?.value ?? '');
  }

  /** Open the recovery card without synthesizing a pointer event (used by visual/accessibility tools). */
  openForgotPassword(): void {
    this.forgotOverlay.visible = true;
    this.forgotEmail?.focus();
  }

  private build(initialUsername: string, initialPassword: string): void {
    for (const ch of this.removeChildren()) ch.destroy({ children: true });
    this.addChild(sceneBackground());
    const enter: Container[] = [];

    // Ambient gold dust drifting up through the scene — subtle page-wide life, behind all content.
    this.addChild(this.buildAmbient());

    // Animated brand hero (spotlight + floating crowned-ace emblem + glowing "Ace Game" wordmark).
    const brand = this.buildBrand();
    this.addChild(brand);
    enter.unshift(brand);

    // A soft glass card grounds the login form on the wood (added before the fields so it sits behind).
    const formPanel = this.buildFormPanel();
    this.addChild(formPanel);
    enter.push(formPanel);

    // Card heading + a short gold underline — the form's own title under the brand.
    const heading = makeText('ورود به حساب کاربری', { size: 25, weight: '700', align: 'center', fill: COLORS.gold });
    heading.anchor.set(0.5, 0);
    heading.position.set(DESIGN_W / 2, 474);
    heading.style.dropShadow = { color: 0x2a1c06, alpha: 0.6, blur: 0, distance: 2, angle: Math.PI / 2 };
    this.addChild(heading);
    this.addChild(new Graphics().roundRect(DESIGN_W / 2 - 66, 512, 132, 2, 1).fill({ color: COLORS.accentDark, alpha: 0.9 }));
    enter.push(heading);

    const fieldW = 540;
    const fieldX = (DESIGN_W - fieldW) / 2;

    this.username = new TextField({
      width: fieldW,
      height: 76,
      value: initialUsername,
      placeholder: 'نام کاربری',
      align: 'right',
      icon: this.ctx.iconTexture('user'),
      accessibilityLabel: 'نام کاربری',
      autocomplete: 'username',
      enterKeyHint: 'next',
    });
    this.username.position.set(fieldX, 542);
    this.username.setReadonly(this.usernameLocked);
    this.addChild(this.username);

    this.password = new TextField({
      width: fieldW,
      height: 76,
      value: initialPassword,
      secure: true,
      placeholder: 'رمز عبور',
      align: 'right',
      icon: this.ctx.iconTexture('lock'),
      accessibilityLabel: 'رمز عبور',
      autocomplete: 'current-password',
      enterKeyHint: 'done',
    });
    this.password.position.set(fieldX, 634);
    this.addChild(this.password);
    this.username.onKeyboardEnter(() => this.password.focus());
    this.password.onKeyboardEnter(() => this.requestLogin());

    this.status = makeText('', { size: 20, align: 'center' });
    this.status.anchor.set(0.5, 0);
    this.status.position.set(DESIGN_W / 2, 728);
    this.addChild(this.status);

    this.loginBtn = new Button('ورود', () => this.requestLogin(), {
      width: fieldW,
      height: 90,
      fontSize: 30,
      icon: this.ctx.iconTexture('enter'),
    });
    this.loginBtn.position.set(fieldX, 760);
    this.addChild(this.loginBtn);

    // Forgot password (opens an email overlay → ForgotPassword(5)) and register (the referral-gated
    // Register flow). Secondary (dark) styling keeps the gold «ورود» as the single primary action.
    const smallW = (fieldW - 20) / 2;
    const forgot = new Button('فراموشی رمز', () => {
      this.forgotOverlay.visible = true;
      this.forgotEmail?.focus();
    }, {
      width: smallW, height: 68, fontSize: 21, fill: COLORS.panel, textColor: COLORS.text, icon: this.ctx.iconTexture('key'),
    });
    forgot.position.set(fieldX, 876);
    this.addChild(forgot);
    const register = new Button('ثبت نام', () => this.ctx.navigate(RoomName.Register), {
      width: smallW, height: 68, fontSize: 21, fill: COLORS.panel, textColor: COLORS.text, icon: this.ctx.iconTexture('ticket'),
    });
    register.position.set(fieldX + smallW + 20, 876);
    this.addChild(register);

    // Footer flourish that grounds the bottom of the screen (kills the old empty wood band).
    const footer = this.buildFooter();
    this.addChild(footer);
    enter.push(footer);

    enter.push(this.username, this.password, this.loginBtn, forgot, register);
    // Play the staggered entrance only on first build, not on every resize re-flow.
    if (!this.built) {
      this.built = true;
      enterFade(enter);
    }

    this.forgotOverlay = this.buildForgotOverlay();
    this.addChild(this.forgotOverlay);
  }

  /**
   * The animated brand hero, returned as one container so the caller can fade it in as a unit.
   * Layers (back→front): a warm radial spotlight that lights the upper third (turning the wood's
   * dark top vignette into an intentional stage light — this is what removed the "empty gap" up top),
   * a pulsing gold halo, the floating crowned-ace emblem, twinkling sparkles, then the "Ace Game"
   * wordmark with a shine glint sweeping across it. All loops are `bind`-ed to scene children, so
   * SceneManager's `destroy({children:true})` self-cancels them — no manual teardown needed.
   */
  private buildBrand(): Container {
    const brand = new Container();
    const cx = DESIGN_W / 2;
    const emblemY = 188;
    const wordY = 312;

    // Warm stage spotlight over the hero. A square region keeps the radial radius predictable; it's
    // centred HIGH (y≈130) and strong so it reaches the very top — that's what lifts the wood texture's
    // dark top vignette (the "gap") into an intentional warm stage light.
    const spot = new Graphics().rect(0, 0, DESIGN_W, DESIGN_W).fill(
      new FillGradient({
        type: 'radial',
        center: { x: 0.5, y: 0.18 },
        innerRadius: 0,
        outerCenter: { x: 0.5, y: 0.18 },
        outerRadius: 0.6,
        colorStops: [
          { offset: 0, color: 'rgba(255,223,150,0.50)' },
          { offset: 0.4, color: 'rgba(255,212,135,0.16)' },
          { offset: 1, color: 'rgba(255,200,120,0)' },
        ],
      }),
    );
    spot.y = -10;
    brand.addChild(spot);
    this.loop(spot, 4600, (t) => {
      spot.alpha = 0.88 + 0.12 * Math.sin(t * Math.PI * 2);
    });

    // Floating emblem with a breathing gold halo behind it. emblemWrap is positioned AT the emblem
    // centre so scaleIn() pops from the centre and the bob moves the whole group together.
    const emblemWrap = new Container();
    emblemWrap.position.set(cx, emblemY);
    brand.addChild(emblemWrap);

    const halo = new Graphics().rect(-180, -180, 360, 360).fill(
      new FillGradient({
        type: 'radial',
        center: { x: 0.5, y: 0.5 },
        innerRadius: 0,
        outerCenter: { x: 0.5, y: 0.5 },
        outerRadius: 0.5,
        colorStops: [
          { offset: 0, color: 'rgba(236,187,52,0.42)' },
          { offset: 0.55, color: 'rgba(236,187,52,0.12)' },
          { offset: 1, color: 'rgba(236,187,52,0)' },
        ],
      }),
    );
    emblemWrap.addChild(halo);
    this.loop(halo, 2600, (t) => {
      const s = (Math.sin(t * Math.PI * 2) + 1) / 2;
      halo.alpha = 0.55 + 0.45 * s;
      halo.scale.set(0.9 + 0.14 * s);
    });

    const logoTex = this.ctx.iconTexture('logo') ?? this.ctx.respackFrame('logo');
    if (logoTex) {
      const size = 224;
      const logo = iconSprite(logoTex, size);
      logo.position.set(-size / 2, -size / 2); // centre the sprite on emblemWrap's origin
      emblemWrap.addChild(logo);
    }
    scaleIn(emblemWrap, 520, 0.45);
    this.loop(emblemWrap, 3200, (t) => {
      emblemWrap.y = emblemY - 8 * Math.sin(t * Math.PI * 2); // gentle vertical float
    });

    // A few twinkling sparkles scattered around the emblem.
    for (const [sx, sy, ss, ph] of [
      [cx - 150, emblemY - 96, 11, 0],
      [cx + 156, emblemY - 60, 9, 0.33],
      [cx + 134, emblemY + 104, 13, 0.66],
      [cx - 138, emblemY + 90, 8, 0.5],
    ] as const) {
      brand.addChild(this.sparkle(sx, sy, ss, ph));
    }

    // "Ace Game" wordmark — vertical gold gradient, dark stroke + drop shadow for a game-logo look.
    const goldFill = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: '#fff4cf' },
        { offset: 0.46, color: '#f4cf5a' },
        { offset: 0.56, color: '#e7af1f' },
        { offset: 1, color: '#bd820d' },
      ],
    });
    const wordmark = new Text({
      text: 'Ace Game',
      style: new TextStyle({
        fontFamily: FONT_FAMILY,
        fontSize: 70,
        fontWeight: '800',
        align: 'center',
        fill: goldFill,
        stroke: { color: 0x2a1c05, width: 7, join: 'round' },
        dropShadow: { color: 0x000000, alpha: 0.55, blur: 8, distance: 5, angle: Math.PI / 2 },
        letterSpacing: 3,
      }),
    });
    wordmark.anchor.set(0.5, 0);
    wordmark.position.set(cx, wordY);
    brand.addChild(wordmark);
    this.loop(wordmark, 3000, (t) => {
      wordmark.scale.set(1 + 0.025 * Math.sin(t * Math.PI * 2)); // subtle breathing
    });

    // Shine glint that sweeps across the wordmark every few seconds (no mask — just a travelling star).
    const halfW = wordmark.width / 2;
    const glint = this.sparkle(cx, wordY + wordmark.height * 0.42, 14, 0);
    glint.alpha = 0;
    brand.addChild(glint);
    this.loop(glint, 4200, (t) => {
      const SWEEP = 0.34; // fraction of the cycle the glint is travelling; hidden the rest
      if (t < SWEEP) {
        const p = t / SWEEP;
        glint.x = cx - halfW + p * halfW * 2;
        glint.alpha = Math.sin(p * Math.PI);
        glint.scale.set(0.7 + 0.6 * Math.sin(p * Math.PI));
      } else {
        glint.alpha = 0;
      }
    });

    // A slim gold divider with a centre diamond, separating the hero from the form below.
    const dy = wordY + wordmark.height + 22;
    const divider = new Container();
    divider.addChild(
      new Graphics()
        .moveTo(cx - 200, dy).lineTo(cx - 16, dy)
        .moveTo(cx + 16, dy).lineTo(cx + 200, dy)
        .stroke({ color: COLORS.panelBorder, width: 2, alpha: 0.6 }),
      new Graphics().regularPoly(cx, dy, 7, 4, Math.PI / 4).fill({ color: COLORS.accent }),
    );
    brand.addChild(divider);

    return brand;
  }

  /**
   * A barely-there glass card behind the login fields — just a thin gold border and a faint sheen.
   * The body fill is kept very low (alpha 0.18) so the full-screen wood grain shows through here too,
   * matching the home page (which lays no dark panel over the wood); it only frames the form, not hides it.
   * Sized tall (452→1012) so the form occupies the screen's center instead of leaving an empty wood band.
   */
  private buildFormPanel(): Container {
    const x = 44;
    const y = 452;
    const w = DESIGN_W - x * 2;
    const h = 560;
    const c = new Container();
    c.addChild(
      new Graphics().roundRect(x, y, w, h, 30).fill({ color: COLORS.panelSoft, alpha: 0.18 }),
      // top sheen — a faint lighter band along the upper edge for a glassy feel
      new Graphics().roundRect(x + 2, y + 2, w - 4, h * 0.4, 28).fill({ color: COLORS.white, alpha: 0.03 }),
      new Graphics().roundRect(x, y, w, h, 30).stroke({ color: COLORS.panelBorder, width: 2, alpha: 0.55 }),
    );
    return c;
  }

  /**
   * Bottom flourish: a gold divider with a centre diamond, a short tagline, and a small «ورود امن»
   * lock chip. Fills the lower screen (which used to be an empty dark wood band) and reads as a footer.
   */
  private buildFooter(): Container {
    const c = new Container();
    const cx = DESIGN_W / 2;
    const dy = viewport.h - 202; // pin to the live bottom (baseline 1280 → 1078, unchanged)
    c.addChild(
      new Graphics()
        .moveTo(cx - 168, dy).lineTo(cx - 14, dy)
        .moveTo(cx + 14, dy).lineTo(cx + 168, dy)
        .stroke({ color: COLORS.panelBorder, width: 2, alpha: 0.5 }),
      new Graphics().regularPoly(cx, dy, 6, 4, Math.PI / 4).fill({ color: COLORS.accent, alpha: 0.85 }),
    );

    const tagline = makeText('تجربه‌ای امن و سریع از بازی کارت آنلاین', { size: 19, align: 'center', fill: COLORS.textDim });
    tagline.anchor.set(0.5, 0);
    tagline.position.set(cx, dy + 18);
    c.addChild(tagline);

    // «ورود امن» with a small lock glyph, centred as a unit (RTL: caption left, lock to its right).
    const row = new Container();
    const cap = makeText('ورود امن', { size: 16, fill: COLORS.textDim });
    cap.anchor.set(0, 0.5);
    cap.position.set(0, 10);
    const lock = iconSprite(this.ctx.iconTexture('lock'), 20);
    lock.position.set(cap.width + 6, 0);
    row.addChild(cap, lock);
    row.position.set(cx - (cap.width + 6 + 20) / 2, dy + 54);
    c.addChild(row);
    return c;
  }

  /** Ambient gold motes that drift slowly upward and fade, looping forever (bound, so they self-cancel). */
  private buildAmbient(): Container {
    const layer = new Container();
    const motes = 9;
    for (let i = 0; i < motes; i++) {
      const col = ((i * 83) % DESIGN_W); // spread across width without RNG (unavailable in this env)
      const size = 2 + (i % 3);
      const span = 360 + (i % 4) * 220; // vertical travel distance
      const baseY = 200 + ((i * 137) % Math.max(120, viewport.h - 300));
      const m = new Graphics().circle(0, 0, size).fill({ color: 0xffe6a8 });
      m.position.set(col, baseY);
      layer.addChild(m);
      const period = 7000 + (i % 5) * 1400;
      this.loop(m, period, (t) => {
        // phase-stagger by index so they don't pulse in unison
        const p = (t + i / motes) % 1;
        m.y = baseY - p * span;
        m.alpha = 0.28 * Math.sin(p * Math.PI); // fade in then out over the rise
      });
    }
    return layer;
  }

  /** Draw a 4-point sparkle star that twinkles (alpha + scale), offset by `phase` (0–1). */
  private sparkle(x: number, y: number, size: number, phase: number): Graphics {
    const t = size * 0.16;
    const g = new Graphics()
      .poly([0, -size, t, -t, size, 0, t, t, 0, size, -t, t, -size, 0, -t, -t])
      .fill({ color: 0xfff3d0 });
    g.position.set(x, y);
    this.loop(g, 1900, (tt) => {
      const s = (Math.sin((tt + phase) * Math.PI * 2) + 1) / 2;
      g.alpha = 0.12 + 0.88 * s;
      g.scale.set(0.55 + 0.55 * s);
    });
    return g;
  }

  /** Run `onUpdate(t∈[0,1])` on a seamless loop until `node` is destroyed (use sin(t·2π) for continuity). */
  private loop(node: Container, period: number, onUpdate: (t: number) => void): void {
    if (prefersReducedMotion()) {
      onUpdate(0.25);
      return;
    }
    const run = (): void => {
      if (node.destroyed) return;
      animate({ duration: period, ease: linear, bind: node, onUpdate, onComplete: run });
    };
    run();
  }

  /**
   * A dim overlay with a centred glass card: key emblem, title, hint, email field → ForgotPassword(5).
   * Tapping the dim backdrop closes it; the card has a hitArea so taps on it don't fall through.
   * The server's reply arrives as a global SendMessage popup.
   */
  private buildForgotOverlay(): Container {
    const ov = new Container();
    ov.visible = false;
    const dim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: SCRIM.dialog });
    dim.eventMode = 'static';
    dim.on('pointertap', () => (ov.visible = false));
    ov.addChild(dim);

    const cardW = 600;
    const cardH = 430;
    const cardX = (DESIGN_W - cardW) / 2;
    const cardY = (viewport.h - cardH) / 2;
    const card = new Container();
    card.eventMode = 'static';
    card.hitArea = new Rectangle(cardX, cardY, cardW, cardH);
    card.addChild(
      new Graphics().roundRect(cardX + 3, cardY + 6, cardW, cardH, 28).fill({ color: COLORS.scrim, alpha: 0.4 }),
      new Graphics().roundRect(cardX, cardY, cardW, cardH, 28).fill({ color: COLORS.panel, alpha: 0.97 }),
      new Graphics().roundRect(cardX + 2, cardY + 2, cardW - 4, cardH * 0.34, 26).fill({ color: COLORS.white, alpha: 0.04 }),
      new Graphics().roundRect(cardX, cardY, cardW, cardH, 28).stroke({ color: COLORS.panelBorder, width: 2, alpha: 0.6 }),
    );
    ov.addChild(card);

    const key = iconSprite(this.ctx.iconTexture('key'), 66);
    key.position.set(DESIGN_W / 2 - 33, cardY + 26);
    ov.addChild(key);

    const title = makeText('بازیابی رمز عبور', { size: 26, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(DESIGN_W / 2, cardY + 100);
    ov.addChild(title);

    const note = makeText('ایمیل حساب خود را وارد کنید تا لینک بازیابی برایتان ارسال شود.', {
      size: 18, align: 'center', fill: COLORS.textDim,
    });
    note.anchor.set(0.5, 0);
    note.style.wordWrap = true;
    note.style.wordWrapWidth = cardW - 88;
    note.position.set(DESIGN_W / 2, cardY + 142);
    ov.addChild(note);

    const fw = 520;
    const fx = (DESIGN_W - fw) / 2;
    const emailField = new TextField({
      width: fw,
      height: 70,
      placeholder: 'ایمیل',
      align: 'right',
      icon: this.ctx.iconTexture('email'),
      accessibilityLabel: 'ایمیل حساب کاربری',
      autocomplete: 'email',
      inputMode: 'email',
      enterKeyHint: 'send',
    });
    emailField.position.set(fx, cardY + 208);
    ov.addChild(emailField);
    this.forgotEmail = emailField;

    const halfW = (fw - 20) / 2;
    const submitForgot = (): void => {
      const email = emailField.value.trim();
      if (!email) {
        this.ctx.dialog({ message: 'لطفاً ایمیل حساب خود را وارد کنید.' });
        return;
      }
      this.ctx.client.send(encodeForgotPassword(email));
      ov.visible = false;
    };
    emailField.onKeyboardEnter(submitForgot);
    const send = new Button(
      'ارسال',
      submitForgot,
      { width: halfW, height: 72, fontSize: 26, icon: this.ctx.iconTexture('enter') },
    );
    send.position.set(fx + halfW + 20, cardY + 300);
    ov.addChild(send);
    const back = new Button('بازگشت', () => (ov.visible = false), {
      width: halfW,
      height: 72,
      fontSize: 26,
      fill: COLORS.panel,
      textColor: COLORS.text,
    });
    back.position.set(fx, cardY + 300);
    ov.addChild(back);
    return ov;
  }

  /**
   * Resolve explicit credentials only for the loopback-only Vite harness. Merely adding user/pass
   * to a hosted URL must never prefill or submit the login form.
   */
  private static credsFromUrl(): InitialLoginCredentials {
    if (typeof location === 'undefined') return loginCredentialsFromSearch('', false);
    const allowDevHarness = isLocalDevelopment(location.hostname, import.meta.env.DEV);
    return loginCredentialsFromSearch(location.search, allowDevHarness);
  }

  private setStatus(text: string, color = COLORS.textDim): void {
    this.status.text = text;
    this.status.style.fill = color;
    if (text) announceStatus(text, color === COLORS.danger);
  }

  /**
   * Telegram storage and LoadLogin are independent asynchronous operations. Remember whichever
   * finishes first, then let trySavedAutoLogin start only once both values agree.
   */
  private async loadSavedCredentialsForAutoLogin(): Promise<void> {
    const credentials = await loadLoginCredentials();
    if (this.destroyed || !this.useSavedCredentials) return;
    this.savedCredentials = credentials;
    if (!credentials) return;

    if (!this.boundUsername) {
      if (!this.username.value) this.username.value = credentials.username;
      if (!this.password.value) this.password.value = credentials.password;
    } else if (this.boundUsername === credentials.username) {
      this.password.value = credentials.password;
    }
    this.trySavedAutoLogin();
  }

  private trySavedAutoLogin(): void {
    const credentials = this.savedCredentials;
    if (!this.useSavedCredentials ||
        !credentials ||
        this.boundUsername !== credentials.username ||
        this.savedAutoLoginStarted ||
        this.loginInProgress) {
      return;
    }

    this.savedAutoLoginStarted = true;
    this.username.value = credentials.username;
    this.password.value = credentials.password;
    this.setStatus('ورود خودکار…');
    this.requestLogin();
  }

  private requestLogin(): void {
    this.loginWhenReady = true;
    if (this.ctx.client.isOpen && !this.ctx.client.isReady) {
      this.setStatus('سرور بازی در حال راه‌اندازی است…');
      return;
    }
    void this.doLogin();
  }

  private resumeLoginWhenReady(): void {
    if (!this.loginWhenReady || this.loginInProgress || !this.ctx.client.isReady) return;
    void this.doLogin();
  }

  private async doLogin(): Promise<void> {
    if (this.loginInProgress) return;
    this.loginInProgress = true;
    this.loginBtn.setEnabled(false);
    try {
      if (!this.ctx.client.isOpen) {
        this.setStatus('در حال اتصال به سرور…');
        await this.ctx.client.connect(this.ctx.client.url);
      }
      if (!this.ctx.client.isReady) {
        this.loginWhenReady = true;
        this.setStatus('سرور بازی در حال راه‌اندازی است…');
        this.loginInProgress = false;
        this.loginBtn.setEnabled(true);
        return;
      }
      this.loginWhenReady = false;
      this.setStatus('در حال ورود…');
      const submittedCredentials = {
        username: this.username.value.trim(),
        password: this.password.value,
      };
      const result = await this.ctx.client.login(submittedCredentials);
      await saveLoginCredentials(result.player.username, submittedCredentials.password);
      gameState.applyLogin(result);
      this.setStatus('ورود موفق ✓', COLORS.gold);
      hapticNotification('success');
      this.ctx.navigate(RoomName.MainMenu);
    } catch (err) {
      if (!this.ctx.client.isReady) {
        this.loginWhenReady = true;
        this.setStatus('سرور بازی در حال راه‌اندازی است…');
        this.loginInProgress = false;
        this.loginBtn.setEnabled(true);
        return;
      }
      // The global SendMessage handler already shows the server's reason as a dialog.
      this.setStatus(err instanceof Error ? err.message : String(err), COLORS.danger);
      hapticNotification('error');
      this.loginInProgress = false;
      this.loginBtn.setEnabled(true);
    }
  }
}
